'use client';
import { Modal } from '@benzinga/core-ui';
import React from 'react';
import { TokenizedExpression } from '../SearchBar';
import { SearchModule } from '../Modules';
import { DragOverlay, useDroppable } from '@dnd-kit/core';
import { AND_NEW_ID, EMPTY_EXPRESSION_ID, EXPRESSION_NEW_ID, NOT_NEW_ID, OR_NEW_ID, TreeExpression } from './entities';
import { TokenizedToTree, TreeToTokenized } from './helpers';
import styled, { TC } from '@benzinga/themetron';
import {
  cloneTree,
  newAndNode,
  newIncompleteExpressionNode,
  newNotNode,
  newOrNode,
  simplifyExpression,
} from './TreeOperations';
import { noop } from '@benzinga/utils';
import { Button } from 'antd';
import {
  ExpressionBuilderSection,
  ExpressionBuilderSectionAnd,
  ExpressionBuilderSectionIncompleteExpression,
  ExpressionBuilderSectionNot,
  ExpressionBuilderSectionOr,
} from './ExpressionBuilderSections';
import { ExpressionBuilderContext, ExpressionBuilderContextProvider } from './ExpressionBuilderContext';

interface ExpressionBuilderProps {
  expression: TokenizedExpression[];
  onExpressionUpdate: (expression: TokenizedExpression[]) => void;
  modules: SearchModule[];
}

interface ExpressionBuilderModalProps {
  visible: boolean;
  onClose: () => void;
}

const EmptyExpressionBuilder: React.FC = () => {
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: EMPTY_EXPRESSION_ID,
  });

  return (
    <EmptyBuilderContainer ref={setDroppableRef}>
      <div style={{ alignSelf: 'center' }}>DROP HERE TO START</div>
    </EmptyBuilderContainer>
  );
};

export const ExpressionBuilderInner: React.FC<ExpressionBuilderProps> = props => {
  const andPlaceHolderNode = React.useMemo(() => newAndNode(AND_NEW_ID), []);
  const orPlaceHolderNode = React.useMemo(() => newOrNode(OR_NEW_ID), []);
  const notPlaceHolderNode = React.useMemo(() => newNotNode(NOT_NEW_ID), []);

  const { actualRoot, applyExpression, displayedRoot, draggedNode, setActualRoot, setDisplayedRoot } =
    React.useContext(ExpressionBuilderContext);

  return (
    <>
      <ExpressionBuilderContainer>
        <ExpressionBuilderItemsContainer>
          <ExpressionBuilderSectionAnd
            modules={[]}
            node={andPlaceHolderNode}
            onExpressionChange={noop}
            parentNode={undefined}
            root={andPlaceHolderNode}
          />
          <ExpressionBuilderSectionOr
            modules={[]}
            node={orPlaceHolderNode}
            onExpressionChange={noop}
            parentNode={undefined}
            root={orPlaceHolderNode}
          />
          <ExpressionBuilderSectionNot
            modules={[]}
            node={notPlaceHolderNode}
            onExpressionChange={noop}
            parentNode={undefined}
            root={notPlaceHolderNode}
          />
          {props.modules.map(module => {
            const node = newIncompleteExpressionNode(module.id, undefined, EXPRESSION_NEW_ID + module.id);
            return (
              <ExpressionBuilderSectionIncompleteExpression
                key={EXPRESSION_NEW_ID + module.id}
                modules={[module]}
                node={node}
                onExpressionChange={noop}
                parentNode={undefined}
                root={node}
              />
            );
          })}
        </ExpressionBuilderItemsContainer>
        <ExpressionBuilderTreeContainer>
          {displayedRoot ? (
            <ExpressionBuilderSection
              modules={props.modules}
              node={displayedRoot}
              onExpressionChange={e => {
                setActualRoot(e);
              }}
              parentNode={undefined}
              root={displayedRoot}
            />
          ) : (
            <EmptyExpressionBuilder />
          )}
        </ExpressionBuilderTreeContainer>
      </ExpressionBuilderContainer>
      <DragOverlay>
        {draggedNode ? (
          <ExpressionBuilderSection
            draggedClone
            modules={props.modules}
            node={draggedNode}
            onExpressionChange={noop}
            parentNode={undefined}
            root={draggedNode}
          />
        ) : null}
      </DragOverlay>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', padding: '20px' }}>
        <Button
          onClick={() => {
            applyExpression(displayedRoot);
          }}
          onMouseEnter={() => {
            setDisplayedRoot(simplifyExpression(cloneTree(actualRoot)));
          }}
          onMouseLeave={() => {
            setDisplayedRoot(cloneTree(actualRoot));
          }}
          type={'primary'}
        >
          Apply
        </Button>
      </div>
    </>
  );
};

const Builder: React.FC<ExpressionBuilderProps> = props => {
  const expression = React.useMemo(() => TokenizedToTree(props.expression), [props.expression]);
  const onExpressionUpdate = React.useCallback(
    (expression: TreeExpression | undefined) => {
      const onExpressionUpdate = props.onExpressionUpdate;
      onExpressionUpdate(TreeToTokenized(expression));
    },
    [props.onExpressionUpdate],
  );

  return (
    <ExpressionBuilderContextProvider expression={expression} onExpressionUpdate={onExpressionUpdate}>
      <ExpressionBuilderInner {...props} />
    </ExpressionBuilderContextProvider>
  );
};

export const ExpressionBuilder: React.FC<ExpressionBuilderProps & ExpressionBuilderModalProps> = props => {
  return (
    <Modal
      fullScreen={true}
      onClose={props.onClose}
      size={{ coverScreen: true }}
      title={
        <div style={{ display: 'flex' }}>
          <div style={{ paddingRight: '0.3em' }}>Expression Builder</div>
          <Badge>BETA</Badge>
        </div>
      }
      visible={props.visible}
    >
      <Builder {...props} />
    </Modal>
  );
};

const ExpressionBuilderContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  overflow: auto;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const ExpressionBuilderItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
  padding: 8px;
  flex-basis: 20%;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
`;

const ExpressionBuilderTreeContainer = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding: 8px;
  flex-basis: 80%;
`;

const EmptyBuilderContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-grow: 1;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background};
`;

const Badge = styled(TC.Row)`
  align-self: center;
  background-color: #008fd9;
  border-radius: 0.5em;
  color: ${({ theme }) => theme.colors.backgroundActive};
  font-size: 0.875em;
  font-weight: bold;
  padding: 0.1em 0.3em;
  text-transform: uppercase;
  height: 1.2em;
  align-items: center;
`;
